<script setup lang="ts">
import { computed, type PropType, ref, reactive } from 'vue'
import LazyVideo from './LazyVideo.vue'

interface MediaItem {
    url: string
    thumbnail_url?: string
}

// 偏移量
const currentIndex = ref(0)

// 视频加载状态映射
const videoLoadedMap = reactive<Record<number, boolean>>({})

const props = defineProps({
    title: {
        type: String,
        default: '',
    },
    desc: {
        type: String,
        default: '',
    },
    video: {
        type: String,
        default: '',
    },
    urls: {
        type: Array as PropType<string[]>,
        default: () => [],
    },
    medias: {
        type: Array as PropType<MediaItem[]>,
        default: () => [],
    },
    title1: {
        type: String,
        default: '',
    },
    title2: {
        type: String,
        default: '',
    },
    video_width: {
        type: String,
        default: '',
    },
})

const hasMedia = computed(() => !!props.video)
const isVideo = computed(() =>
    props.video ? /\.(mp4|webm|ogg)(\?.*)?$/i.test(props.video) : false
)

// 判断媒体项是否是视频
const isMediaVideo = (url: string) => /\.(mp4|webm|ogg)(\?.*)?$/i.test(url)

// 视频加载完成回调
const handleVideoLoaded = (index: number) => {
    videoLoadedMap[index] = true
}

// 添加禁用状态判断
const canGoLeft = computed(() => currentIndex.value > 0)
const canGoRight = computed(() => currentIndex.value < props.medias.length - 1)

const goLeft = () => {
    if (canGoLeft.value) {
        currentIndex.value = 0
    }
}

const goRight = () => {
    if (canGoRight.value) {
        currentIndex.value = 1
    }
}
</script>

<template>
    <div class="tools-introduce">
        <p v-if="title1" class="title text-white">
            {{ title1 }}
        </p>
        <p
            v-if="title2"
            class="text-base max-w-4xl mx-auto mt-5 mb-20 text-text-adad text-center"
        >
            {{ title2 }}
        </p>
        <h2
            class="title text-white"
            :class="{ '!text-[3rem]': title1 }"
        >
            {{ title }}
        </h2>
        <p class="desc">{{ desc }}</p>
        <div v-if="hasMedia" class="video">
            <LazyVideo
                :style="{ width: video_width ? video_width : '' }"
                v-if="isVideo"
                :src="video"
            />
            <img v-else :src="video" :alt="title || 'Video upscaler introduction'" loading="lazy" />
        </div>
        <div v-else-if="medias.length > 0" class="urls" :class="{ 'single-item': medias.length === 1 }">
            <div
                class="container"
                :style="medias.length > 1 ? { transform: `translateX(${currentIndex * -560}px)` } : {}"
                style="max-width: 100%"
            >
                <div v-for="(media, index) in medias" :key="index" class="media-item">
                    <!-- 封面图片：视频未加载时显示 -->
                    <img
                        v-if="media.thumbnail_url && isMediaVideo(media.url) && !videoLoadedMap[index]"
                        :src="media.thumbnail_url"
                        :alt="title || 'Video upscaler thumbnail'"
                        class="media-cover"
                    />
                    <!-- 视频：加载完成后显示 -->
                    <LazyVideo
                        v-if="isMediaVideo(media.url)"
                        :src="media.url"
                        @canplay="handleVideoLoaded(index)"
                    />
                    <!-- 图片：直接显示 -->
                    <img
                        v-else
                        :src="media.url"
                        :alt="title || 'Video upscaler media'"
                        loading="lazy"
                    />
                </div>
            </div>
            <!-- 只有多个媒体项时才显示轮播按钮 -->
            <div v-if="medias.length > 1" class="transferBTN-container">
                <div
                    class="left transform-btn"
                    :class="{ disabled: !canGoLeft }"
                    @click="goLeft"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="23"
                        viewBox="0 0 11 23"
                        fill="none"
                        style="transform: rotate(180deg)"
                    >
                        <path
                            d="M1.5 21L8.1103 13.1878C9.05675 12.0693 9.05676 10.4307 8.1103 9.31217L1.5 1.5"
                            :stroke="canGoLeft ? 'white' : '#67676a'"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
                <div
                    class="right transform-btn"
                    :class="{ disabled: !canGoRight }"
                    @click="goRight"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="23"
                        viewBox="0 0 11 23"
                        fill="none"
                    >
                        <path
                            d="M1.5 21L8.1103 13.1878C9.05675 12.0693 9.05676 10.4307 8.1103 9.31217L1.5 1.5"
                            :stroke="canGoRight ? 'white' : '#67676a'"
                            stroke-width="3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="less" scoped>
.tools-introduce {
    // max-width: 1000px;
    margin: 180px auto;

    .title {
        font-size: 3.75rem;
        font-weight: 700;
        text-align: center;
        max-width: 1000px;
        margin: 0 auto;
        line-height: 1.2;
    }
    .desc {
        color: #adadad;
        font-size: 1rem;
        font-weight: 400;
        padding: 12px 0 60px;
        max-width: 1000px;
        margin: 0 auto;
    }
    .video {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 auto;

        video,
        img {
            height: 562px;
            width: 1000px;
            object-fit: cover;
            border-radius: 16px;
        }
    }
    .urls {
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        
        // 单项时居中显示，尺寸更大
        &.single-item {
            .container {
                justify-content: center;
                
                .media-item {
                    width: 800px;
                    height: 450px;
                }
            }
        }
        
        .container {
            display: flex;
            column-gap: 10px;
            transition: transform 0.5s ease-in-out;
            width: fit-content;

            .media-item {
                position: relative;
                width: 480px;
                height: 280px;
                flex-shrink: 0;

                video, img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 16px;
                }

                video {
                    position: relative;
                    z-index: 0;
                }

                .media-cover {
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: 1;
                }
            }
        }
    }

    .transferBTN-container {
        width: 150px;
        margin: 30px auto;
        display: flex;
        justify-content: space-between;

        .transform-btn {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background-color: #22242d;
            color: #67676a;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover:not(.disabled) {
                background-color: #22242d;
                color: #fff;
            }

            &.disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }
        }
    }

    @media (max-width: 768px) {
        margin: 60px auto 40px;

        .title {
            font-size: 2rem;
            padding: 0 16px;
        }

        .desc {
            font-size: 0.875rem;
            padding: 12px 16px 30px;
        }

        .video {
            video,
            img {
                width: 100%;
                height: auto;
                max-height: 300px;
                border-radius: 12px;
            }
        }

        .urls {
            &.single-item {
                .container {
                    .media-item {
                        width: 100%;
                        max-width: 480px;
                        height: 270px;
                    }
                }
            }
            
            .container {
                column-gap: 8px;

                .media-item {
                    width: 280px;
                    height: 160px;

                    video, img {
                        border-radius: 12px;
                    }
                }
            }
        }

        .transferBTN-container {
            width: 120px;
            margin: 20px auto;

            .transform-btn {
                width: 28px;
                height: 28px;
            }
        }
    }

    @media (max-width: 480px) {
        margin: 40px auto 30px;

        .title {
            font-size: 1.5rem;
        }

        .desc {
            font-size: 0.8rem;
        }

        .urls {
            &.single-item {
                .container {
                    .media-item {
                        max-width: 100%;
                        height: 200px;
                    }
                }
            }
            
            .container {
                .media-item {
                    width: 240px;
                    height: 140px;
                }
            }
        }
    }
}
</style>
